import React from "react";
import { logger } from "../../utils";

/**
 * Metadata which can be returned from the component which renders the MediaStream
 */
export interface StreamMetadata {}

/**
 * Interface which can be extended to add custom Props to MediaStream rendering component
 */
export interface MediaPlaybackProps<T extends StreamMetadata> {
  onMetadataChange?: (metadata: T) => void;
}

export interface MediaProps {
  stream: MediaStream;
}

interface InnerProps {
  track: MediaStreamTrack;
}

/**
 * HOC which converts MediaStreamTrack to MediaStream.
 *
 * Example usage:
 * interface CustomStreamMetadata { width: number; height: number }
 * interface CustomMediaPlaybackProps { fullscreen: boolean }
 *
 * withMediaStreamTrack<CustomStreamMetadata, CustomMediaPlaybackProps>(ComponentWhichRendersMediaStream);
 *
 * @param Component component which takes MediaStream in props
 */
export const withMediaStreamTrack = <M extends StreamMetadata, P extends MediaPlaybackProps<M>>(
  Component: React.ComponentType<MediaProps & P>
) => {
  type WithMediaStreamTrackProps = InnerProps & P;

  class WithMediaStreamTrack extends React.Component<WithMediaStreamTrackProps> {
    private readonly stream: MediaStream;

    constructor(props: WithMediaStreamTrackProps) {
      super(props);
      this.stream = new MediaStream();
    }

    componentDidMount() {
      logger.debug("WithMediaStreamTrack: Mounting component", this.props.track);
      this.setStreamTrack(this.props.track);
    }

    componentDidUpdate(
      prevProps: Readonly<WithMediaStreamTrackProps>,
      prevState: Readonly<WithMediaStreamTrackProps>
    ) {
      if (this.props.track.id !== prevProps.track.id) {
        logger.debug("WithMediaStreamTrack: updating stream with a new track", this.props.track);
        this.stream.removeTrack(prevProps.track);
        prevProps.track.removeEventListener("ended", this.handleEnded);
        this.setStreamTrack(this.props.track);
      }
    }

    componentWillUnmount() {
      this.stream.removeTrack(this.props.track);
    }

    render() {
      return <Component stream={this.stream} {...this.props} />;
    }

    private setStreamTrack = (track: MediaStreamTrack): void => {
      this.stream.addTrack(track);
      track.addEventListener("ended", this.handleEnded);
    };

    private handleEnded = (event: Event) =>
      this.stream.removeTrack(event.target as MediaStreamTrack);
  }

  return WithMediaStreamTrack;
};
